import { apiFetch } from './client';

// Types mirror the Go response shape (importJobResponse). Status values
// mirror the imports.import_job CHECK constraint. Keep aligned with
// internal/services/imports/service.go.

export type ImportStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface ImportContentProgress {
  discovered: number;
  processed: number;
  skipped: number;
  failed: number;
}

export interface ImportJob {
  id: string;
  organization_id: string;
  actor_user_id?: string;
  source_url: string;
  status: ImportStatus;
  progress: Record<string, ImportContentProgress>;
  error_message?: string;
  last_heartbeat_at?: string;
  attempts: number;
  started_at?: string;
  finished_at?: string;
  created_at: string;
  updated_at: string;
}

interface ImportListResponse {
  imports: ImportJob[];
}

export async function startImport(
  token: string | null,
  slug: string,
  sourceURL: string,
): Promise<ImportJob> {
  return apiFetch<ImportJob>(`/api/v1/organizations/${slug}/imports`, {
    method: 'POST',
    body: { source_url: sourceURL },
    token,
  });
}

export async function getImport(
  token: string | null,
  slug: string,
  jobID: string,
): Promise<ImportJob> {
  return apiFetch<ImportJob>(
    `/api/v1/organizations/${slug}/imports/${jobID}`,
    { token },
  );
}

export async function listImports(
  token: string | null,
  slug: string,
): Promise<ImportJob[]> {
  const res = await apiFetch<ImportListResponse>(
    `/api/v1/organizations/${slug}/imports`,
    { token },
  );
  return res.imports ?? [];
}

export async function cancelImport(
  token: string | null,
  slug: string,
  jobID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${slug}/imports/${jobID}/cancel`,
    { method: 'POST', token },
  );
}

// Helper — sum a single counter type across all content types. Used by the
// wizard's overall progress bar so it doesn't have to know about each
// content type separately.
export function totalProcessed(job: ImportJob): number {
  let n = 0;
  for (const k of Object.keys(job.progress)) {
    n += job.progress[k]?.processed ?? 0;
  }
  return n;
}

export function totalDiscovered(job: ImportJob): number {
  let n = 0;
  for (const k of Object.keys(job.progress)) {
    n += job.progress[k]?.discovered ?? 0;
  }
  return n;
}
